/**
 * 特定商取引法に基づく表記 — the 14 items of design/08 §5 (T11-44) mapped onto the structured
 * fields of cms-service's `tokushoho` policy (`services/cms/app/services/policy_schema.py`, T09-26).
 * Only short labels + a help key live here; the legal wording itself is the owner's / 行政書士's
 * (the law: 消費者庁 guide, linked below — docs/research/japan-tax-legal.md §7, "ยังไม่ยืนยัน"
 * until the 行政書士 check of T13).
 *
 * Completeness is a count of empty boxes (not money): an item is filled when every cms field it
 * uses has Japanese text (a shared field — phone, e-mail — once for every language), and a seed
 * sample `[[…]]` counts as empty (cms does the same).
 */
export interface TokushohoItem {
  /** ① … ⑭ */
  no: number;
  /** i18n key under `settings:tokushoho.items.*` (label + one-line help). */
  key: string;
  /** cms field names of the `tokushoho` policy that make up the item. */
  fields: readonly string[];
}

export const TOKUSHOHO_ITEMS: readonly TokushohoItem[] = [
  { no: 1, key: "seller", fields: ["business_name"] },
  { no: 2, key: "representative", fields: ["representative"] },
  { no: 3, key: "address", fields: ["address"] },
  { no: 4, key: "phone", fields: ["phone", "phone_hours"] },
  { no: 5, key: "email", fields: ["email"] },
  { no: 6, key: "price", fields: ["price_display"] },
  { no: 7, key: "other_fees", fields: ["additional_fees"] },
  { no: 8, key: "payment_methods", fields: ["payment_methods"] },
  { no: 9, key: "payment_timing", fields: ["payment_timing"] },
  { no: 10, key: "delivery_timing", fields: ["delivery_timing"] },
  { no: 11, key: "returns", fields: ["return_policy"] },
  { no: 12, key: "quantity", fields: ["quantity_limit"] },
  { no: 13, key: "other", fields: ["other_notes"] },
  { no: 14, key: "validity", fields: ["application_validity"] },
];

export const RETURNS_ITEM = 11;
export const PAYMENT_ITEM = 8;
export const REQUIRED_LOCALE = "ja";

/** Official explanations (消費者庁 通信販売 / 返品特約 guideline; 警視庁 古物商 website display). */
export const LAW_LINKS = {
  tokushoho: "https://www.no-trouble.caa.go.jp/what/mailorder/",
  returns: "https://www.no-trouble.caa.go.jp/pdf/20230421la02_07.pdf",
  kobutsu:
    "https://www.keishicho.metro.tokyo.lg.jp/tetsuzuki/kobutsu/kaisetsu/kobutsu_shinsei.html",
} as const;

const PLACEHOLDER = /\[\[[^[\]]{1,120}\]\]/;

export type PolicyFields = Readonly<Record<string, Readonly<Record<string, string>> | undefined>>;

export function fieldText(fields: PolicyFields, name: string, locale: string): string {
  const value = fields.shared?.[name] ?? fields[locale]?.[name] ?? "";
  return PLACEHOLDER.test(value) ? "" : value.trim();
}

export function itemFilled(item: TokushohoItem, fields: PolicyFields): boolean {
  return item.fields.every((name) => fieldText(fields, name, REQUIRED_LOCALE) !== "");
}

export interface Completeness {
  filled: number;
  total: number;
  percent: number;
  missing: TokushohoItem[];
  /** ⑪ 返品特約 has no Japanese text → buyers may cancel within 8 days (特商法 15条の3). */
  returnsMissing: boolean;
}

export function completeness(fields: PolicyFields): Completeness {
  const missing = TOKUSHOHO_ITEMS.filter((item) => !itemFilled(item, fields));
  const total = TOKUSHOHO_ITEMS.length;
  const filled = total - missing.length;
  return {
    filled,
    total,
    percent: Math.floor((filled * 100) / total),
    missing,
    returnsMissing: missing.some((item) => item.no === RETURNS_ITEM),
  };
}

function normalise(text: string): string {
  return text
    .normalize("NFKC")
    .replace(/[\s、,・/]+/g, "")
    .toLowerCase();
}

/**
 * ⑧ vs the ways to pay that are switched on (cms `GET /admin/policies/tokushoho/suggestions`
 * builds that text from payment-service): `true` when both have text and they differ.
 */
export function paymentsDiffer(current: string, suggested: string | undefined): boolean {
  if (!suggested?.trim() || !current.trim()) {
    return false;
  }
  return normalise(current) !== normalise(suggested);
}
